/**
 * PackModelChip — the compact "your model · ready · trained {date} · {n} photos" pill for the
 * pack page's owner hero (copy spec §3 state D). New, presentation-only component (narcis_funnel
 * plan, Step 1) — deliberately NOT `ModelCard` (components/ModelCard): that card is a shared,
 * two-line rail card mounted by StudioRail/StudioSheet with its own contact strip and "use other
 * photos" affordance; touching its default copy would ripple to those unrelated surfaces. This
 * component exists solely to render the one-line canon string the copy spec specifies, reusing
 * `formatTrainedAt` (ModelCard's own exported date formatter — single source for "12 July") so
 * the two surfaces never drift on date formatting even though their shapes differ.
 *
 * Canvas: `Pack Page States.html` state D `.modelchip` / `Pack Page Mobile.html` `.mchip`.
 */
import {Link} from 'react-router'
import {formatTrainedAt} from '@product/narcis/components/ModelCard/ModelCard'
import styles from './PackModelChip.module.scss'
import {intakeFromLink} from '@product/narcis/path'

interface PackModelChipProps {
    trainedAt: string
    photoCount: number
    /** D-del override (copy spec §3c): "rendered this · {date}" replaces the default
     *  "ready · trained {date} · {n} photos" meta line once this specific pack has been rendered.
     *  Absent (default) renders byte-identically to before. */
    metaOverride?: string
}

export function PackModelChip({trainedAt, photoCount, metaOverride}: PackModelChipProps) {
    return (
        <Link to={intakeFromLink('pack')} className={styles.chip}>
            <svg width="20" height="20" viewBox="0 0 64 64" fill="none" aria-hidden="true" className={styles.mark}>
                <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" />
                <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(60 32 32)" />
                <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(120 32 32)" />
                <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(180 32 32)" />
                <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(240 32 32)" />
                <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(300 32 32)" />
                <circle cx="32" cy="32" r="9" fill="#E8C24A" />
            </svg>
            <span className={styles.text}>
                <b className={styles.name}>your model</b>
                <i className={styles.meta}>
                    {metaOverride ?? (
                        <>
                            <span className={styles.metaReady}>ready · </span>
                            created {formatTrainedAt(new Date(trainedAt))} · {photoCount} photos
                        </>
                    )}
                </i>
            </span>
            <span className={styles.readyTag}><span className={styles.dot} aria-hidden="true" />ready</span>
        </Link>
    )
}
